import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, TextField, Button, Paper,
  MenuItem, Select, FormControl, InputLabel, CircularProgress,
  Alert, Chip, LinearProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import { useAuth } from '../../../contexts/AuthContext';
import { useIsApplabAdmin, useApplabConfig } from '../hooks/useAdminData';
import { APPLAB_ACCENT, APPLAB_ORANGE } from '../constants';
import { MODEL_META } from '../constants/pricing';

const MODELS = Object.entries(MODEL_META).map(([value, m]) => ({ value, ...m }));

function fmtTokens(n) {
  if (!n) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000)     return (n / 1_000).toFixed(1) + 'K';
  return String(n);
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function TestGatewayPage() {
  const navigate  = useNavigate();
  const { currentUser } = useAuth();
  const { isAdmin, loading: adminLoading } = useIsApplabAdmin();
  const config    = useApplabConfig();

  const [model,     setModel]     = useState('claude-sonnet-4-6');
  const [system,    setSystem]    = useState('');
  const [prompt,    setPrompt]    = useState('');
  const [maxTokens, setMaxTokens] = useState(1024);
  const [loading,   setLoading]   = useState(false);
  const [result,    setResult]    = useState(null);
  const [error,     setError]     = useState(null);

  if (adminLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress sx={{ color: APPLAB_ACCENT }} />
    </Box>
  );

  if (!isAdmin) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <Alert severity="error">Du har inte behörighet.</Alert>
    </Box>
  );

  async function run() {
    if (!prompt.trim()) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/.netlify/functions/applab-gateway', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          block: 'ai.chat',
          params: {
            model,
            max_tokens: Number(maxTokens),
            messages: [{ role: 'user', content: prompt }],
            ...(system ? { system } : {}),
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Okänt fel'); return; }
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const month      = currentMonth();
  const cfgTokens  = config?.month === month ? (config?.tokens_used_this_month || 0) : 0;
  const cfgLimit   = config?.total_monthly_limit || 10_000_000;
  const cfgPct     = Math.min(100, Math.round((cfgTokens / cfgLimit) * 100));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#111', color: 'white', pb: 6 }}>
      <Box sx={{ bgcolor: '#1a1a1a', borderBottom: '1px solid rgba(255,255,255,0.06)', px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box onClick={() => navigate('/applab')} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', color: '#888', '&:hover': { color: 'white' }, fontSize: '0.85rem' }}>
            <ArrowBackIcon sx={{ fontSize: 16 }} /> AppLAB
          </Box>
          <Typography sx={{ color: '#444' }}>›</Typography>
          <Typography fontWeight={700} fontSize="0.9rem">
            App<span style={{ color: APPLAB_ORANGE }}>LAB</span> · Gateway-test
          </Typography>
        </Box>
      </Box>

      <Container maxWidth="md" sx={{ mt: 4 }}>

        {/* Kvot-status */}
        {config && (
          <Paper sx={{ p: 2.5, bgcolor: '#1a1a1a', borderRadius: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography fontSize="0.8rem" sx={{ color: '#888' }}>Total månadskvot</Typography>
              <Typography fontSize="0.8rem" sx={{ color: '#888' }}>
                {fmtTokens(cfgTokens)} / {fmtTokens(cfgLimit)} tokens
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate" value={cfgPct}
              sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.08)',
                '& .MuiLinearProgress-bar': { bgcolor: cfgPct > 80 ? '#f44336' : APPLAB_ACCENT } }}
            />
            {config.ai_chat_enabled === false && (
              <Alert severity="warning" sx={{ mt: 1.5, bgcolor: '#2e2400', color: '#ffb74d' }}>
                ai.chat är pausat av administratör.
              </Alert>
            )}
          </Paper>
        )}

        {/* Form */}
        <Paper sx={{ p: 3, bgcolor: '#1a1a1a', borderRadius: 2, mb: 3 }}>
          <Typography fontWeight={700} sx={{ mb: 2, color: 'white' }}>Testa ai.chat</Typography>

          <FormControl size="small" sx={{ mb: 1.5, minWidth: 320 }}>
            <InputLabel sx={{ color: '#888', '&.Mui-focused': { color: APPLAB_ACCENT } }}>Modell</InputLabel>
            <Select
              value={model}
              onChange={e => setModel(e.target.value)}
              label="Modell"
              sx={{
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: APPLAB_ACCENT },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: APPLAB_ACCENT },
                '& .MuiSvgIcon-root': { color: '#888' },
              }}
              MenuProps={{ PaperProps: { sx: { bgcolor: '#1e1e1e', color: 'white', '& .MuiMenuItem-root:hover': { bgcolor: APPLAB_ACCENT + '22' } } } }}
            >
              {MODELS.map(m => (
                <MenuItem key={m.value} value={m.value} sx={{ color: 'white', flexDirection: 'column', alignItems: 'flex-start', py: 1.2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{m.label}</span>
                    {m.recommended && (
                      <Chip label="Rekommenderad" size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: APPLAB_ACCENT + '33', color: APPLAB_ACCENT }} />
                    )}
                  </Box>
                  <Typography fontSize="0.72rem" sx={{ color: '#666', mt: 0.3 }}>{m.description}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {MODEL_META[model]?.warning && (
            <Alert severity="info" sx={{ mb: 2, bgcolor: '#1a1a2e', color: '#90CAF9', '& .MuiAlert-icon': { color: '#90CAF9' }, fontSize: '0.8rem' }}>
              {MODEL_META[model].warning} Välj Sonnet 4.6 för de flesta uppgifter.
            </Alert>
          )}

          <TextField
            label="System-prompt (valfri)"
            value={system}
            onChange={e => setSystem(e.target.value)}
            multiline rows={2} fullWidth size="small"
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { color: 'white', bgcolor: 'rgba(255,255,255,0.03)' }, '& label': { color: '#888' } }}
          />

          <TextField
            label="Prompt"
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            multiline rows={4} fullWidth size="small"
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { color: 'white', bgcolor: 'rgba(255,255,255,0.03)' }, '& label': { color: '#888' } }}
          />

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label="max_tokens"
              value={maxTokens}
              onChange={e => setMaxTokens(e.target.value)}
              type="number" size="small"
              sx={{ width: 130, '& .MuiOutlinedInput-root': { color: 'white' }, '& label': { color: '#888' } }}
            />
            <Button
              variant="contained"
              onClick={run}
              disabled={loading || !prompt.trim()}
              startIcon={loading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <SendIcon />}
              sx={{ bgcolor: APPLAB_ACCENT, '&:hover': { bgcolor: '#00796B' } }}
            >
              {loading ? 'Skickar...' : 'Skicka'}
            </Button>
          </Box>
        </Paper>

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, bgcolor: '#2e0000', color: '#ef9a9a' }}>{error}</Alert>
        )}

        {/* Result */}
        {result && (
          <Paper sx={{ p: 3, bgcolor: '#1a1a1a', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Chip label={result.model} size="small" sx={{ bgcolor: APPLAB_ACCENT + '22', color: APPLAB_ACCENT, fontSize: '0.7rem' }} />
              <Chip label={`In: ${fmtTokens(result.usage?.tokens_in)}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#888', fontSize: '0.7rem' }} />
              <Chip label={`Ut: ${fmtTokens(result.usage?.tokens_out)}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#888', fontSize: '0.7rem' }} />
              <Chip label={`$${(result.usage?.cost_usd || 0).toFixed(5)}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#888', fontSize: '0.7rem' }} />
            </Box>
            <Typography
              component="pre"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.85rem', color: '#ddd', lineHeight: 1.7 }}
            >
              {result.content}
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
}
